import { Check, ChevronDown } from "lucide-react";
import { Popover } from "./ui";

/*
 * A small choice inside a field, where its unit or label would be (e.g. "inc GST", "minutes"):
 * the choice and a chevron, opening a menu of the options (each with a line saying what it means,
 * the current one ticked). Locked: just the choice. Put it in a TextField's `suffix`.
 */

export interface InlineOption<T extends string> {
    value: T;
    label: string;
    help?: string;
}

export function InlinePicker<T extends string>(
    props: Readonly<{
        /** What it chooses, for screen readers and on hover. */
        label: string;
        options: readonly InlineOption<T>[];
        value: T;
        /** Shown when the value isn't one of the options. */
        fallback?: string;
        disabled?: boolean;
        onChange: (value: T) => void;
    }>,
) {
    const shown = props.options.find((o) => o.value === props.value)?.label ?? props.fallback ?? props.value;
    if (props.disabled) return <span className="text-(length:--label-text) text-muted">{shown}</span>;
    return (
        <Popover
            label={props.label}
            width="w-56"
            buttonClassName="pointer-events-auto -mr-1 inline-flex cursor-pointer items-center gap-0.5 rounded px-1 py-0.5 text-(length:--label-text) text-muted hover:bg-surface-2 hover:text-ink"
            button={
                <>
                    {shown}
                    <ChevronDown className="size-3.5" />
                </>
            }
        >
            {(close) =>
                props.options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-surface-2"
                        onClick={() => {
                            close();
                            if (o.value !== props.value) props.onChange(o.value);
                        }}
                    >
                        <span className="min-w-0 flex-1">
                            <span className="block">{o.label}</span>
                            {o.help ? <span className="block text-xs text-muted">{o.help}</span> : null}
                        </span>
                        {o.value === props.value ? <Check className="size-4 shrink-0 text-brand-600" /> : null}
                    </button>
                ))
            }
        </Popover>
    );
}
